/**
 * Transit detail dialog showing comprehensive transit and observability information.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  Science as ScienceIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckBox as CompleteIcon,
  IndeterminateCheckBox as PartialIcon,
  Cancel as CancelIcon,
  NightsStay as MoonIcon,
} from '@mui/icons-material';
import { formatTime, formatDuration, getLocalTime } from '../utils/timeFormatting';
import { calculateTransitObservability } from '../utils/observabilityCalculations';
import QualityBadge from './QualityBadge';
import EphemerisSourceChip from './EphemerisSourceChip';

const TransitInfoCard = ({ transit }) => (
  <Card>
    <CardContent>
      <Typography variant="subtitle1" gutterBottom color="primary">
        <ScienceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Transit Information
      </Typography>
      <Divider sx={{ my: 1 }} />
      <List dense>
        <ListItem>
          <ListItemText primary="Host Star" secondary={transit.exoplanet.host_star} />
        </ListItem>
        <ListItem>
          <ListItemText primary="V Magnitude" secondary={transit.exoplanet.vmag?.toFixed(1) || 'N/A'} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Transit Depth" secondary={`${transit.transit.depth_percent?.toFixed(2) || 'N/A'}%`} />
        </ListItem>
        <DataSourceItem transit={transit} />
        <TimingUncertaintyItems transit={transit} />
        {transit.transit.light_time_correction_minutes && (
          <ListItem>
            <ListItemText
              primary="Light-Time Correction"
              secondary={`${transit.transit.light_time_correction_minutes.toFixed(1)} minutes applied`}
            />
          </ListItem>
        )}
        <ListItem>
          <ListItemText primary="Duration" secondary={formatDuration(transit.transit.duration_hours)} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Period" secondary={`${transit.exoplanet.period_days?.toFixed(2) || 'N/A'} days`} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Maximum Altitude" secondary={`${transit.transit.altitude?.toFixed(0) || 'N/A'}`} />
        </ListItem>
        <MoonInfoItems transit={transit} />
      </List>
    </CardContent>
  </Card>
);

const DataSourceItem = ({ transit }) => {
  const source = transit.exoplanet.ephemeris_source;
  const meta = transit.exoplanet.exoclock_metadata;
  const raw = transit.exoplanet.data_sources;

  // Build provenance — use stored data_sources or infer from ephemeris_source
  const provenance = {
    base: raw?.base || 'NASA',
    ephemeris: raw?.ephemeris || source || 'NASA',
    duration: raw?.duration || (source === 'ExoFOP-TESS' ? 'ExoFOP-TESS' : 'NASA'),
    depth: raw?.depth || 'NASA',
  };

  const entries = [
    { label: 'Base data', source: provenance.base },
    { label: 'Ephemeris', source: provenance.ephemeris },
    { label: 'Duration', source: provenance.duration },
    { label: 'Depth', source: provenance.depth },
  ];

  const allSources = [...new Set(Object.values(provenance))];
  const ephemerisIsExoClock = provenance.ephemeris === 'ExoClock';

  return (
    <>
      <ListItem>
        <ListItemText
          primary="Data Sources"
          secondary={
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              {allSources.map(s => (
                <Chip key={s} label={s} size="small" variant="outlined"
                  color={s === 'ExoClock' ? 'success' : s === 'ExoFOP-TESS' ? 'info' : 'default'}
                />
              ))}
            </Box>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Data Provenance"
          secondary={
            <Box component="span" display="flex" flexDirection="column" gap={0.25}>
              {entries.map(({ label, source: src }) => (
                <Box key={label} component="span" display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" sx={{ minWidth: 70 }}>{label}:</Typography>
                  <Typography variant="caption" fontWeight="bold">{src}</Typography>
                  <EphemerisSourceChip source={src} />
                </Box>
              ))}
            </Box>
          }
        />
      </ListItem>
      {(transit.exoplanet.quality_grade || transit.exoplanet.quality_score != null) && (
        <ListItem>
          <ListItemText
            primary="Quality"
            secondary={
              <Box display="flex" alignItems="center" gap={1}>
                <QualityBadge
                  qualityGrade={transit.exoplanet.quality_grade}
                  qualityScore={transit.exoplanet.quality_score}
                  ephemerisSource={source}
                  observationCount={meta?.total_observations}
                />
                {transit.exoplanet.quality_score != null && (
                  <span>Score: {transit.exoplanet.quality_score}/100</span>
                )}
              </Box>
            }
          />
        </ListItem>
      )}
      {meta && <ExoClockMetadataItems meta={meta} ephemerisFromExoClock={ephemerisIsExoClock} />}
    </>
  );
};

const ExoClockMetadataItems = ({ meta, ephemerisFromExoClock }) => (
  <>
    <Divider sx={{ my: 1 }} />
    <ListItem>
      <ListItemText
        primary="ExoClock Community Data"
        secondary={
          <Typography variant="caption" color="text.secondary">
            {ephemerisFromExoClock
              ? 'ExoClock provided the ephemeris for this target'
              : 'Ephemeris from another source; ExoClock provides observation tracking for this target'}
          </Typography>
        }
      />
    </ListItem>
    {meta.priority && (
      <ListItem>
        <ListItemText
          primary="ExoClock Priority"
          secondary={
            <Chip
              label={meta.priority}
              size="small"
              color={meta.priority === 'alert' ? 'error' :
                     meta.priority === 'high' ? 'warning' :
                     meta.priority === 'medium' ? 'info' : 'default'}
            />
          }
        />
      </ListItem>
    )}
    {meta.current_oc_min != null && (
      <ListItem>
        <ListItemText
          primary="O-C (Observed minus Computed)"
          secondary={`${meta.current_oc_min.toFixed(2)} minutes`}
        />
      </ListItem>
    )}
    {meta.total_observations != null && (
      <ListItem>
        <ListItemText
          primary="Total Observations"
          secondary={`${meta.total_observations} (${meta.recent_observations || 0} recent)`}
        />
      </ListItem>
    )}
    {meta.exoclock_observations != null && meta.exoclock_observations > 0 && (
      <ListItem>
        <ListItemText
          primary="ExoClock Network Observations"
          secondary={meta.exoclock_observations}
        />
      </ListItem>
    )}
    {meta.min_telescope_inches != null && (
      <ListItem>
        <ListItemText
          primary="Min Telescope Size"
          secondary={`${meta.min_telescope_inches}" (${(meta.min_telescope_inches * 25.4).toFixed(0)} mm)`}
        />
      </ListItem>
    )}
    {meta.depth_mmag != null && (
      <ListItem>
        <ListItemText
          primary="Transit Depth (R-band)"
          secondary={`${meta.depth_mmag.toFixed(1)} mmag`}
        />
      </ListItem>
    )}
    {meta.last_synced && (
      <ListItem>
        <ListItemText
          primary="ExoClock Last Synced"
          secondary={new Date(meta.last_synced).toLocaleDateString()}
        />
      </ListItem>
    )}
  </>
);

const TimingUncertaintyItems = ({ transit }) => {
  if (!transit.transit.timing_uncertainty) return null;

  return (
    <>
      <ListItem>
        <ListItemText
          primary="Timing Uncertainty"
          secondary={
            transit.transit.timing_uncertainty.minutes
              ? `+/-${transit.transit.timing_uncertainty.minutes.toFixed(0)} minutes (1-sigma)`
              : 'No uncertainty data'
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Ephemeris Quality"
          secondary={
            <Chip
              label={transit.transit.timing_uncertainty.quality || 'Unknown'}
              size="small"
              color={
                transit.transit.timing_uncertainty.quality === 'excellent' ? 'success' :
                transit.transit.timing_uncertainty.quality === 'good' ? 'primary' :
                transit.transit.timing_uncertainty.quality === 'fair' ? 'warning' : 'error'
              }
            />
          }
        />
      </ListItem>
      {transit.transit.timing_uncertainty.warning && (
        <ListItem>
          <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
          <ListItemText
            primary="Warning"
            secondary={transit.transit.timing_uncertainty.warning}
            secondaryTypographyProps={{ color: 'warning.main' }}
          />
        </ListItem>
      )}
    </>
  );
};

const MoonInfoItems = ({ transit }) => {
  if (transit.transit.moon_separation == null) return null;

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <ListItem>
        <ListItemIcon><MoonIcon /></ListItemIcon>
        <ListItemText primary="Moon Separation" secondary={`${transit.transit.moon_separation.toFixed(1)}`} />
      </ListItem>
      <ListItem>
        <ListItemText primary="Moon Illumination" secondary={`${transit.transit.moon_illumination}%`} />
      </ListItem>
      <ListItem>
        <ListItemText
          primary="Moon Altitude"
          secondary={
            transit.transit.moon_altitude <= 0
              ? `${transit.transit.moon_altitude.toFixed(1)} (below horizon)`
              : `${transit.transit.moon_altitude.toFixed(1)}`
          }
        />
      </ListItem>
      {transit.transit.required_moon_separation != null && (
        <ListItem>
          <ListItemText
            primary="Required Separation"
            secondary={`>= ${transit.transit.required_moon_separation} (Lorentzian model)`}
          />
        </ListItem>
      )}
    </>
  );
};

const KeyTimesCard = ({ transit, observationStart, observationEnd, baselineBefore, baselineAfter }) => {
  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom color="primary">
          <TimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Key Times (UTC)
        </Typography>
        <Divider sx={{ my: 1 }} />
        <List dense>
          <ListItem>
            <ListItemText
              primary="Observation Start"
              secondary={`${formatTime(observationStart)} (${baselineBefore} min before ingress)`}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Transit Ingress"
              secondary={formatTime(ingressTime)}
              secondaryTypographyProps={{ fontWeight: 'bold' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Transit Midpoint"
              secondary={formatTime(midpointTime)}
              secondaryTypographyProps={{ fontWeight: 'bold' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Transit Egress"
              secondary={formatTime(egressTime)}
              secondaryTypographyProps={{ fontWeight: 'bold' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Observation End"
              secondary={`${formatTime(observationEnd)} (${baselineAfter} min after egress)`}
            />
          </ListItem>
          <ObservationWindowItems transit={transit} />
        </List>
      </CardContent>
    </Card>
  );
};

const ObservationWindowItems = ({ transit }) => {
  if (!transit.observation_window) return null;

  return (
    <>
      {transit.observation_window.extended_window_reason && (
        <ListItem>
          <ListItemIcon><InfoIcon color="info" /></ListItemIcon>
          <ListItemText
            primary="Extended Window"
            secondary={transit.observation_window.extended_window_reason}
            secondaryTypographyProps={{ color: 'info.main' }}
          />
        </ListItem>
      )}
      {transit.observation_window.warning && (
        <ListItem>
          <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
          <ListItemText
            primary="Observation Warning"
            secondary={transit.observation_window.warning}
            secondaryTypographyProps={{ color: 'warning.main' }}
          />
        </ListItem>
      )}
    </>
  );
};

const ObservatoryVisibilityCard = ({
  transit,
  observatories,
  baselineBefore,
  baselineAfter,
  minAltitude,
}) => {
  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom color="primary">
          <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Observatory Visibility
        </Typography>
        <Divider sx={{ my: 1 }} />
        {observatories.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Observatory</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Can Observe</TableCell>
                  <TableCell align="center">Ingress</TableCell>
                  <TableCell align="center">Midpoint</TableCell>
                  <TableCell align="center">Egress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {observatories.map((obs) => {
                  const observability = calculateTransitObservability(transit, obs, {
                    baselineBefore, baselineAfter, minAltitude,
                  });
                  const ingressTimes = getLocalTime(ingressTime, obs);
                  const midpointTimes = getLocalTime(midpointTime, obs);
                  const egressTimes = getLocalTime(egressTime, obs);

                  return (
                    <TableRow key={obs._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{obs.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption">
                            {obs.location?.city || 'Unknown'}, {obs.location?.country || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                            {obs.location?.timezone || 'No timezone'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {observability.canObserve === 'complete' && <CompleteIcon color="success" fontSize="small" />}
                          {observability.canObserve === 'partial' && <PartialIcon color="warning" fontSize="small" />}
                          {observability.canObserve === 'no' && <CancelIcon color="error" fontSize="small" />}
                          <Typography variant="caption">{observability.reason}</Typography>
                        </Box>
                      </TableCell>
                      <TimeDisplayCell times={ingressTimes} />
                      <TimeDisplayCell times={midpointTimes} isPrimary />
                      <TimeDisplayCell times={egressTimes} />
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No observatories configured
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const TimeDisplayCell = ({ times, isPrimary }) => (
  <TableCell align="center">
    <Box>
      <Typography
        variant="caption"
        fontWeight="bold"
        display="block"
        color={isPrimary ? 'primary' : undefined}
      >
        {times.local || 'N/A'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {times.utc} UTC
      </Typography>
    </Box>
  </TableCell>
);

const TransitDetailDialog = ({
  open,
  onClose,
  transit,
  observatories,
  observationPlans,
  baselineBefore,
  baselineAfter,
  filters,
}) => {
  if (!transit) return null;

  const midpointTime = new Date(transit.transit.time_utc);
  const halfDurationMs = (transit.transit.duration_hours || 0) * 3600000 / 2;
  const ingressTime = new Date(midpointTime.getTime() - halfDurationMs);
  const egressTime = new Date(midpointTime.getTime() + halfDurationMs);

  // Find matching plan for altitude-adjusted times
  const transitId = `${transit.exoplanet.name}_${new Date(transit.transit.time_utc).getTime()}`;
  const matchingPlan = observationPlans.find(plan => {
    const planTransitId = `${plan.target.name}_${new Date(plan.timing.transit_midpoint).getTime()}`;
    return planTransitId === transitId;
  });

  const observationStart = matchingPlan?.observationStartTime || new Date(ingressTime.getTime() - (baselineBefore * 60000));
  const observationEnd = matchingPlan?.observationEndTime || new Date(egressTime.getTime() + (baselineAfter * 60000));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Transit Details: {transit.exoplanet?.name}
          </Typography>
          <IconButton onClick={onClose}>
            <CancelIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TransitInfoCard transit={transit} />
          </Grid>
          <Grid item xs={12} md={6}>
            <KeyTimesCard
              transit={transit}
              observationStart={observationStart}
              observationEnd={observationEnd}
              baselineBefore={baselineBefore}
              baselineAfter={baselineAfter}
            />
          </Grid>
          <Grid item xs={12}>
            <ObservatoryVisibilityCard
              transit={transit}
              observatories={observatories}
              baselineBefore={baselineBefore}
              baselineAfter={baselineAfter}
              minAltitude={filters.minAltitude}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransitDetailDialog;
